/**
 * AnalysisFilter — one control for the two analysis dimensions: WHICH
 * model and WHICH statistic. The model track on top ("All models" then
 * provider groups — logo-only chips that reveal the variant name when
 * selected, shared prefix stripped, hairline dividers, no nested
 * capsules); the statistic slider (All /
 * Highest / Average / Lowest / pass metrics) as a second row in normal
 * flow. Active = solid ink chip; square corners throughout.
 */

import {
  modelLogoSrc,
  PASS_METRIC_ORDER,
  passMetricLabel,
  SERIES_LABELS,
  SERIES_ORDER,
  type PassMetric,
  type ScoreSeries,
} from './benchmarkAnalysis'
import { cn } from '@/lib/utils'

/** k values the pass-metric chips offer — up to 8 because the smallest
 * per-model lane in current exports has 9 rollouts. */
const PASS_K_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8]

interface AnalysisFilterProps {
  models: string[]
  /** null = all models. */
  model: string | null
  onModel: (model: string | null) => void
  series: ScoreSeries[]
  onSeries: (series: ScoreSeries[]) => void
  /** Selected pass metric — non-null overrides the score series. */
  passMetric: PassMetric | null
  onPassMetric: (metric: PassMetric | null) => void
  passK: number
  onPassK: (k: number) => void
}

interface ProviderGroup {
  logoSrc: string | null
  models: string[]
}

function groupByProvider(models: string[]): ProviderGroup[] {
  const groups = new Map<string, ProviderGroup>()
  for (const model of models) {
    const logo = modelLogoSrc(model)
    const key = logo ?? `ungrouped:${model}`
    const group = groups.get(key)
    if (group === undefined) {
      groups.set(key, { logoSrc: logo, models: [model] })
    } else {
      group.models.push(model)
    }
  }
  return Array.from(groups.values())
}

/** Strip the longest common dash-prefix within a group: gpt-5.4-mini / gpt-oss → 5.4-mini / oss. */
function variantLabels(models: string[]): Map<string, string> {
  if (models.length < 2) {
    return new Map(models.map((model) => [model, model]))
  }
  const split = models.map((model) => model.split('-'))
  let common = 0
  while (split.every((parts) => parts[common] !== undefined && parts[common] === split[0]?.[common])) {
    common++
  }
  return new Map(
    models.map((model, index) => {
      const rest = split[index]?.slice(common).join('-') ?? model
      return [model, rest === '' ? model : rest]
    })
  )
}

const chipClasses = (active: boolean): string =>
  cn(
    'inline-flex items-center gap-1.5 whitespace-nowrap rounded px-2.5 py-1 font-mono text-xs transition-colors',
    active
      ? 'bg-foreground text-background shadow-sm'
      : 'text-muted-foreground hover:text-foreground'
  )

function Chip({
  active,
  onClick,
  children,
  label,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
  label: string
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={label}
      title={label}
      onClick={onClick}
      className={chipClasses(active)}
    >
      {children}
    </button>
  )
}

/**
 * A pass-metric chip that IS a dropdown: the visible chip shows
 * pass@{k}; the click-through native select on top opens the k picker
 * (1–8). Pressing it also activates the metric, so one click both
 * selects pass@k and offers the k choice.
 */
function PassChip({
  metric,
  active,
  k,
  onActivate,
  onK,
}: {
  metric: PassMetric
  active: boolean
  k: number
  onActivate: () => void
  onK: (k: number) => void
}): React.JSX.Element {
  return (
    <span className="relative inline-flex">
      <span aria-hidden="true" className={chipClasses(active)}>
        {passMetricLabel(metric, k)}
      </span>
      <select
        aria-label={`Choose k for ${passMetricLabel(metric, k)}`}
        value={k}
        // Activate on press so opening the dropdown already selects the
        // metric; picking the current k again then still "took".
        onMouseDown={onActivate}
        onChange={(event) => {
          onActivate()
          onK(Number(event.target.value))
        }}
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        {PASS_K_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {passMetricLabel(metric, option)}
          </option>
        ))}
      </select>
    </span>
  )
}

interface StatBarProps {
  series: ScoreSeries[]
  onSeries: (series: ScoreSeries[]) => void
  passMetric: PassMetric | null
  onPassMetric: (metric: PassMetric | null) => void
  passK: number
  onPassK: (k: number) => void
}

/**
 * The statistic slider that hangs under the selected model chip: one
 * radio across All / Highest / Average / Lowest / pass@k / pass^k.
 * A pass metric replaces the score series (they answer the same "which
 * y-value per task" question); picking a score statistic clears it.
 */
function StatBar({
  series,
  onSeries,
  passMetric,
  onPassMetric,
  passK,
  onPassK,
}: StatBarProps): React.JSX.Element {
  const statValue =
    passMetric ?? (series.length === SERIES_ORDER.length ? 'all' : (series[0] ?? 'best'))
  const selectScoreStat = (next: ScoreSeries[]): void => {
    onPassMetric(null)
    onSeries(next)
  }
  return (
    <div
      role="group"
      aria-label="Score statistic"
      className="inline-flex max-w-full flex-wrap items-center rounded-md border border-border bg-card p-0.5 shadow-sm"
    >
      <Chip
        active={statValue === 'all'}
        onClick={() => { selectScoreStat([...SERIES_ORDER]) }}
        label="All statistics"
      >
        All
      </Chip>
      {SERIES_ORDER.map((key) => (
        <Chip
          key={key}
          active={statValue === key}
          onClick={() => { selectScoreStat([key]) }}
          label={SERIES_LABELS[key]}
        >
          {SERIES_LABELS[key]}
        </Chip>
      ))}
      <span aria-hidden="true" className="mx-1 h-5 w-px shrink-0 bg-border" />
      {PASS_METRIC_ORDER.map((metric) => (
        <PassChip
          key={metric}
          metric={metric}
          active={statValue === metric}
          k={passK}
          onActivate={() => { onPassMetric(metric) }}
          onK={onPassK}
        />
      ))}
    </div>
  )
}

const Divider = (): React.JSX.Element => (
  <span aria-hidden="true" className="mx-1.5 h-5 w-px shrink-0 bg-border" />
)

export function AnalysisFilter({
  models,
  model,
  onModel,
  series,
  onSeries,
  passMetric,
  onPassMetric,
  passK,
  onPassK,
}: AnalysisFilterProps): React.JSX.Element {
  const groups = groupByProvider(models)
  const stat: StatBarProps = { series, onSeries, passMetric, onPassMetric, passK, onPassK }

  return (
    // flex-col, not space-y: both children are inline-flex, so in a plain
    // block container they share one line whenever they fit (few-model
    // benchmarks) and stack only when they overflow — always stack.
    <div className="flex flex-col items-start gap-2">
      <div
        role="group"
        aria-label="Model filter"
        className="inline-flex max-w-full flex-wrap items-center rounded-md border border-border bg-muted/40 p-1"
      >
        <Chip active={model === null} onClick={() => { onModel(null) }} label="All models">
          All models
        </Chip>
        {groups.map((group) => {
          const labels = variantLabels(group.models)
          return (
            <div key={group.models.join('|')} className="flex items-center gap-0.5">
              <Divider />
              {/* Logo-only until selected: with many models the full
                  logo+name chips overflow the track, so the variant name
                  shows only on the active chip (aria-label + title keep
                  the full name on every chip). Logo-less models always
                  show text — there is nothing else to show. */}
              {group.models.map((entry) => (
                <Chip
                  key={entry}
                  active={model === entry}
                  onClick={() => { onModel(entry) }}
                  label={entry}
                >
                  {group.logoSrc !== null && (
                    <img
                      src={group.logoSrc}
                      alt=""
                      // Active chip is solid ink: sit the mark on a small
                      // white plate so dark logos (OpenAI) stay visible.
                      // A silhouette filter is wrong here — tile-shaped
                      // marks (DeepSeek) collapse into a blank square.
                      className={cn(
                        'size-4 shrink-0 object-contain',
                        model === entry && 'rounded bg-background p-0.5'
                      )}
                    />
                  )}
                  {(group.logoSrc === null || model === entry) && (labels.get(entry) ?? entry)}
                </Chip>
              ))}
            </div>
          )
        })}
      </div>
      {/* In normal flow rather than hanging under the selected chip: the
          absolutely-positioned bar went offscreen when an edge chip was
          selected near the viewport boundary. */}
      <StatBar {...stat} />
    </div>
  )
}
